import { useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  FolderOpen,
  GitBranch,
  Network,
  SlidersHorizontal,
  Table2,
  type LucideIcon
} from 'lucide-react';
import { useAuth } from '../../features/auth/AuthContext';
import { UserAccountMenu } from '../UserAccountMenu';
import { useSimulationStore } from '../../store/simulationStore';
import { useVersionStore } from '../../store/versionStore';
import {
  DEFAULT_WORKSPACE_VIEW,
  WORKSPACE_VIEWS,
  useViewStore,
  type WorkspaceView,
  type WorkspaceViewIcon
} from '../../store/viewStore';

const WORKSPACE_VIEW_ICONS: Record<WorkspaceViewIcon, LucideIcon> = {
  network: Network,
  sliders: SlidersHorizontal,
  table: Table2,
  branch: GitBranch,
  chart: BarChart3
};

const applyWorkspaceViewParam = (params: URLSearchParams, view: WorkspaceView) => {
  if (view === DEFAULT_WORKSPACE_VIEW) {
    params.delete('view');
    return;
  }

  params.set('view', view);
};

export const WorkspaceNavigation = () => {
  const location = useLocation();
  const activeView = useViewStore((state) => state.activeView);
  const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
  const currentVersionId = useSimulationStore((state) => state.currentVersionId);
  const isSimulationsActive = location.pathname.startsWith('/simulations');
  const isWorkspaceActive = location.pathname === '/workspace';

  const createWorkspaceViewHref = useCallback((view: WorkspaceView) => {
    const params = new URLSearchParams(isWorkspaceActive ? location.search : '');

    if (!isWorkspaceActive && currentSimulationId) {
      params.set('simulation', currentSimulationId);
      if (currentVersionId) {
        params.set('version', currentVersionId);
      }
    }

    applyWorkspaceViewParam(params, view);
    const query = params.toString();

    return `/workspace${query ? `?${query}` : ''}`;
  }, [currentSimulationId, currentVersionId, isWorkspaceActive, location.search]);

  return (
    <nav className="workspace-nav-rail" aria-label="Workspace navigation">
      <div className="workspace-nav-list">
        <Link
          to="/simulations"
          className={`workspace-nav-link workspace-nav-link-secondary ${isSimulationsActive ? 'active' : ''}`}
          aria-current={isSimulationsActive ? 'page' : undefined}
          aria-label="Models"
          title="Models"
        >
          <FolderOpen size={15} strokeWidth={2.25} aria-hidden="true" />
          <span>Models</span>
        </Link>
        {WORKSPACE_VIEWS.map((view) => {
          const Icon = WORKSPACE_VIEW_ICONS[view.icon];
          const isActive = isWorkspaceActive && activeView === view.id;

          return (
            <Link
              key={view.id}
              to={createWorkspaceViewHref(view.id)}
              className={`workspace-nav-link ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={view.label}
              title={`${view.label}: ${view.description}`}
            >
              <Icon size={15} strokeWidth={2.25} aria-hidden="true" />
              <span>{view.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export const AppHeader = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isWorkspace = location.pathname === '/workspace';

  const autoSaveStatus = useSimulationStore((state) => state.autoSaveStatus);
  const lastAutoSaveAt = useSimulationStore((state) => state.lastAutoSaveAt);
  const autoSaveError = useSimulationStore((state) => state.autoSaveError);
  const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
  const currentSimulationName = useSimulationStore((state) => state.currentSimulationName);
  const currentAccessLevel = useSimulationStore((state) => state.currentAccessLevel);
  const isExample = useVersionStore((state) => state.isExample);

  const isReadOnlyMode = useMemo(() => {
    if (!currentSimulationId) return false;
    return currentAccessLevel === 'viewer' || isExample(currentSimulationId);
  }, [currentSimulationId, currentAccessLevel, isExample]);

  const formatSavedTime = useCallback((iso: string) => {
    const date = new Date(iso);
    const now = new Date();
    const isSameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    return new Intl.DateTimeFormat('en-US', isSameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    ).format(date);
  }, []);

  const autoSaveLabel = useMemo(() => {
    if (isReadOnlyMode) {
      return currentAccessLevel === 'viewer' ? 'Viewing only' : 'Example (read-only)';
    }
    if (autoSaveStatus === 'saving') return 'Saving...';
    if (autoSaveStatus === 'error') return autoSaveError ?? 'Auto-save failed';
    if (lastAutoSaveAt) return `Saved ${formatSavedTime(lastAutoSaveAt)}`;
    return 'All changes saved';
  }, [autoSaveError, autoSaveStatus, currentAccessLevel, formatSavedTime, isReadOnlyMode, lastAutoSaveAt]);

  const autoSaveStatusClass = useMemo(() => {
    if (isReadOnlyMode) return 'viewing-only';
    return autoSaveStatus === 'idle' ? 'saved' : autoSaveStatus;
  }, [autoSaveStatus, isReadOnlyMode]);

  const autoSaveTitle = useMemo(() => {
    if (autoSaveStatus === 'error') return autoSaveError ?? undefined;
    return lastAutoSaveAt ? new Date(lastAutoSaveAt).toLocaleString() : undefined;
  }, [autoSaveError, autoSaveStatus, lastAutoSaveAt]);

  return (
    <header className="app-header">
      <div className="header-left">
        <div className="header-title">
          <Link to="/" className="app-logo-link">
            <div className="app-logo-container">
              <img src="/images/Simcarlo Logo.png" alt="Simcarlo Logo" className="app-logo" />
            </div>
            <span className="app-logo-text">
              <span className="app-logo-text-main">Sim</span>
              <span className="app-logo-text-accent">carlo</span>
            </span>
          </Link>
        </div>
        {user && isWorkspace && (
          <nav className="workspace-breadcrumb" aria-label="Workspace breadcrumb">
            <span className="workspace-breadcrumb-current" title={currentSimulationName}>
              {currentSimulationName || 'Workspace'}
            </span>
          </nav>
        )}
      </div>
      <div className="header-right">
        {user && isWorkspace && (
          <div
            className={`auto-save-indicator ${autoSaveStatusClass}`}
            title={autoSaveTitle}
            role="status"
            aria-live="polite"
          >
            {autoSaveLabel}
          </div>
        )}
        {user && <UserAccountMenu />}
      </div>
    </header>
  );
};
